import type { FC, ReactNode } from 'react'
import type { SearchOptions } from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { Fragment, useState } from 'react'
import {
    CircleUser,
    Heart,
    Menu,
    Search
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { parseFilters } from '@/lib/search'
import { useSavedHomes } from '@/hooks/use-saved-homes'
import { Logo } from '@/components/generals/logo'
import { SearchDialog } from '@/components/search/search-dialog'
import { describePrice } from '@/components/search/search-fields'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuLinkItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

import { navLinks } from '@/constants/site'

interface SiteHeaderProps {
    searchOptions?: SearchOptions | null
    showSearch?: boolean
    wide?: boolean
}

export const SiteHeader: FC<SiteHeaderProps> = ({ searchOptions, showSearch = true, wide = false }): ReactNode => {
    const router = useRouter()
    const { ids, ready } = useSavedHomes()
    const [searchOpen, setSearchOpen] = useState(false)

    const filters = router.pathname === '/homes' ? parseFilters(router.query) : null
    const area = filters?.area ? searchOptions?.areas.find(item => item.slug === filters.area) : undefined
    const typeLabel = filters?.types.length
        ? searchOptions?.types.filter(type => filters.types.includes(type.toLowerCase())).join(', ')
        : ''
    const priceLabel = filters ? describePrice(filters.minPrice, filters.maxPrice) : ''
    const canSearch = Boolean(searchOptions) && showSearch

    const where = area
        ? <span data-mdlfy-cms={`areas:${area.id}:name`}>{area.name}</span>
        : filters?.q || 'Anywhere'

    return (
        <header className='sticky top-0 z-40 border-b border-border bg-white/95 backdrop-blur supports-backdrop-filter:bg-white/85'>
            <div className={cn('mx-auto flex h-18 items-center gap-3 px-4 sm:px-6 md:h-20 lg:px-10', wide ? 'max-w-none' : 'max-w-[1760px]')}>
                <Logo compact={canSearch} />

                <div className='flex min-w-0 flex-1 justify-center'>
                    {canSearch && searchOptions && (
                        <button
                            type='button'
                            onClick={() => setSearchOpen(true)}
                            className='group flex w-full max-w-md min-w-0 items-center rounded-full border border-border bg-white py-1.5 pr-1.5 pl-5 text-left text-sm shadow-pill transition hover:shadow-float focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                            aria-label='Open search'
                        >
                            <span className='min-w-0 flex-1 truncate font-semibold'>{where}</span>
                            <span className='mx-3 hidden h-6 w-px bg-border sm:block' aria-hidden='true' />
                            <span className='hidden max-w-32 truncate text-muted-foreground sm:block'>{typeLabel || 'Any type'}</span>
                            <span className='mx-3 hidden h-6 w-px bg-border sm:block' aria-hidden='true' />
                            <span className='mr-3 hidden truncate text-muted-foreground sm:block'>{priceLabel || 'Any price'}</span>
                            <span className='bg-brand-gradient ml-2 flex size-9 shrink-0 items-center justify-center rounded-full text-white'>
                                <Search className='size-4' strokeWidth={2.5} aria-hidden='true' />
                            </span>
                        </button>
                    )}
                </div>

                <nav className='flex shrink-0 items-center gap-1' aria-label='Main'>
                    <Link
                        href='/sell'
                        className='hidden rounded-full px-4 py-2.5 text-sm font-semibold transition hover:bg-muted lg:block'
                    >
                        Sell your home
                    </Link>

                    <Link
                        href='/style-match'
                        className='hidden rounded-full px-4 py-2.5 text-sm font-semibold transition hover:bg-muted xl:block'
                    >
                        Style match
                    </Link>

                    <Link
                        href='/saved'
                        className='relative hidden size-11 items-center justify-center rounded-full transition hover:bg-muted md:flex'
                        aria-label={ready && ids.length ? `Saved homes, ${ids.length} saved` : 'Saved homes'}
                    >
                        <Heart className='size-5' aria-hidden='true' />
                        {ready && ids.length > 0 && (
                            <span className='absolute top-1 right-1 flex min-w-4.5 items-center justify-center rounded-full bg-primary px-1 text-[0.65rem] leading-4.5 font-bold text-white'>
                                {ids.length}
                            </span>
                        )}
                    </Link>

                    <DropdownMenu>
                        <DropdownMenuTrigger
                            className='flex h-11 items-center gap-2.5 rounded-full border border-border bg-white pr-1.5 pl-3.5 transition hover:shadow-pill focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-popup-open:shadow-pill'
                            aria-label='Open menu'
                        >
                            <Menu className='size-4' aria-hidden='true' />
                            <CircleUser className='size-7 text-muted-foreground' strokeWidth={1.5} aria-hidden='true' />
                        </DropdownMenuTrigger>

                        <DropdownMenuContent>
                            {navLinks.map((link, index) => (
                                <Fragment key={link.href}>
                                    {index === 3 && <DropdownMenuSeparator />}
                                    <DropdownMenuLinkItem
                                        render={<Link href={link.href} />}
                                        className={cn(router.pathname === link.href && 'font-semibold')}
                                    >
                                        {link.label}
                                        {link.href === '/saved' && ready && ids.length > 0 && (
                                            <span className='ml-auto rounded-full bg-primary px-2 text-xs leading-5 font-bold text-white'>{ids.length}</span>
                                        )}
                                    </DropdownMenuLinkItem>
                                </Fragment>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </nav>
            </div>

            {searchOptions && (
                <SearchDialog
                    open={searchOpen}
                    onOpenChange={setSearchOpen}
                    options={searchOptions}
                    keep={filters
                        ? {
                            category: filters.category,
                            styles: filters.styles,
                            amenities: filters.amenities,
                            baths: filters.baths,
                            sort: filters.sort
                        }
                        : undefined}
                    initial={filters
                        ? {
                            q: filters.q,
                            area: filters.area,
                            types: filters.types,
                            minPrice: filters.minPrice,
                            maxPrice: filters.maxPrice,
                            beds: filters.beds
                        }
                        : undefined}
                />
            )}
        </header>
    )
}
